/**
 * The two borrower emails, with today's formats:
 *   - Best-rates grid (the Pricing module's Term × Program email): each cell is the best rate
 *     across the picked lenders, re-priced on every send.
 *   - Options (the Other Lenders email): the shortlisted options, sent as shown or re-priced on
 *     a schedule. Neither email names a lender.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  MANA_EMAIL_TEMPLATES,
  MANA_EMAIL_TEMPLATE_LABELS,
  MANA_SCHEDULE_MIN_INTERVAL_MINUTES,
  REALTOR_COLUMNS,
  REALTOR_ROWS,
  UWM_PROGRAMS,
  fieldValueList,
  programKeysForBuydowns,
  realtorOverParLabel,
  realtorPointsLabel,
  stateName,
  type RealtorContactsSummary,
  type LenderRateRow,
  type ManaEmailTemplate,
  type ManaGridParams,
  type ManaPricingSource,
  type RealtorCell,
  type RealtorPicks,
  MANA_EMAIL_TRANSPORT_LABELS,
  type ManaEmailTransport,
} from '@mana/shared';
import { api } from '../../lib/api.js';
import { Button } from '../../ui/Button.js';
import { Badge } from '../../ui/Card.js';
import { EmailPreviewDialog, type PreviewContent } from './EmailPreview.js';
import {
  SCHEDULES_KEY,
  errText,
  fmtInterval,
  fmtWhen,
  isoToLocalInput,
  localInputToIso,
  trayKey,
  type CanonicalValues,
  type Msg,
  type PreviewDto,
  type ProductType,
  type ScheduleDto,
  type SchedulesResponse,
} from './shared.js';

/** What both emails need from the scenario at send time. */
export interface EmailScenario {
  borrowerName: string;
  productType: ProductType;
  inputs: CanonicalValues;
  uwmInputs: Record<string, unknown> | null;
  lenders: ManaPricingSource[];
  cypBps: number;
  uwmCategory: string | null;
  params: ManaGridParams;
  /** The best-rates template the MLO chose (null = the scenario's default). */
  template: ManaEmailTemplate | null;
  /** Rates pinned for the Realtor Marketing table. */
  realtorPicks: RealtorPicks;
}

function useInterval(existing: ScheduleDto | null) {
  const [intervalN, setIntervalN] = useState('1');
  const [intervalUnit, setIntervalUnit] = useState<'hours' | 'days'>('days');
  const [startLocal, setStartLocal] = useState('');
  useEffect(() => {
    if (!existing) {
      setIntervalN('1');
      setIntervalUnit('days');
      setStartLocal('');
      return;
    }
    setStartLocal(isoToLocalInput(existing.nextRunAt));
    const m = existing.intervalMinutes;
    if (m % 1440 === 0) {
      setIntervalN(String(m / 1440));
      setIntervalUnit('days');
    } else {
      setIntervalN(String(Math.round(m / 60)));
      setIntervalUnit('hours');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id]);
  const intervalMinutes = Math.round((Number(intervalN) || 0) * (intervalUnit === 'days' ? 1440 : 60));
  const intervalOk = Number.isFinite(intervalMinutes) && intervalMinutes >= MANA_SCHEDULE_MIN_INTERVAL_MINUTES;
  return { intervalN, setIntervalN, intervalUnit, setIntervalUnit, startLocal, setStartLocal, intervalMinutes, intervalOk };
}

function ScheduleControls({
  iv,
  existing,
  blockedReason,
  ready,
  title,
  pending,
  onStart,
  msg,
  what,
}: {
  iv: ReturnType<typeof useInterval>;
  existing: ScheduleDto | null;
  blockedReason: string | null;
  ready: boolean;
  title: string;
  pending: boolean;
  onStart: () => void;
  msg: Msg;
  what: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3 text-xs">
      <span className="font-semibold uppercase tracking-wide text-slate-500">Recurring:</span>
      <span className="text-slate-600">{what} every</span>
      <input type="number" min={1} value={iv.intervalN} onChange={(e) => iv.setIntervalN(e.target.value)} className="w-14 rounded border border-slate-300 px-1.5 py-0.5 text-slate-800" />
      <select value={iv.intervalUnit} onChange={(e) => iv.setIntervalUnit(e.target.value as 'hours' | 'days')} className="rounded border border-slate-300 px-1.5 py-0.5 text-slate-800">
        <option value="hours">hours</option>
        <option value="days">days</option>
      </select>
      <span className="text-slate-600">{existing ? 'next send' : 'starting'}</span>
      <input
        type="datetime-local"
        value={iv.startLocal}
        onChange={(e) => iv.setStartLocal(e.target.value)}
        title="Optional — leave blank to start now (first email sends immediately)"
        className="rounded border border-slate-300 px-1.5 py-0.5 text-slate-800"
      />
      {iv.startLocal && (
        <button type="button" onClick={() => iv.setStartLocal('')} className="text-slate-400 hover:text-slate-600" title="Clear the start — first email sends immediately">
          clear
        </button>
      )}
      <Button size="sm" variant="secondary" disabled={!ready || !!blockedReason || pending} title={blockedReason ?? title} onClick={onStart}>
        {pending ? (existing ? 'Updating…' : 'Starting…') : existing ? 'Update scheduled email' : 'Start recurring emails'}
      </Button>
      {msg ? (
        <span className={msg.tone === 'err' ? 'text-rose-600' : 'text-slate-500'}>{msg.text}</span>
      ) : blockedReason ? (
        <span className="text-amber-700">{blockedReason}</span>
      ) : (
        <span className="text-slate-400">
          {existing
            ? 'Sends the saved scenario — Save in the panel to change what it sends · update here for the recipient, interval or start · no email now'
            : iv.startLocal
              ? 'First send at the chosen start · then repeats · min every 4h'
              : 'First sends immediately · leave start blank · min every 4h'}
        </span>
      )}
    </div>
  );
}

function scheduleMessage(r: { schedule: ScheduleDto; sentTo?: string; updated?: boolean; scheduledStart?: string }, minutes: number): string {
  return r.updated
    ? `Schedule updated — next send ${fmtWhen(r.schedule.nextRunAt)}.`
    : r.scheduledStart
      ? `Scheduled — first send ${fmtWhen(r.schedule.nextRunAt)}, then every ${fmtInterval(minutes)}.`
      : `Scheduled — first sent to ${r.sentTo}, repeats every ${fmtInterval(minutes)}.`;
}

// ---------------------------------------------------------------------------
// Best-rates grid email
// ---------------------------------------------------------------------------

/** Which sending connections the MLO has saved under Integrations: Gmail SMTP and/or SendGrid. */
function useEmailTransports(): { configured: Record<ManaEmailTransport, boolean>; loaded: boolean } {
  const q = useQuery<{ gmail_smtp?: { configured: boolean }; sendgrid?: { configured: boolean } }>({
    queryKey: ['integrations-status'],
    queryFn: () => api('/api/integrations'),
    staleTime: 60_000,
  });
  return { configured: { gmail: q.data?.gmail_smtp?.configured ?? true, sendgrid: q.data?.sendgrid?.configured ?? false }, loaded: !!q.data };
}

/** A card's "Via" choice: the schedule's saved connection when one exists, else Gmail. */
function useTransportChoice(existing: ScheduleDto | null): [ManaEmailTransport, (t: ManaEmailTransport) => void, ReturnType<typeof useEmailTransports>] {
  const transports = useEmailTransports();
  const [transport, setTransport] = useState<ManaEmailTransport>(existing?.transport ?? 'gmail');
  useEffect(() => {
    if (existing?.transport) setTransport(existing.transport);
  }, [existing?.transport]);
  return [transport, setTransport, transports];
}

/** "Via: Gmail | SendGrid" — the connection a card's Email now and its recurring schedule send through. */
function TransportSelect({ value, onChange, transports }: { value: ManaEmailTransport; onChange: (t: ManaEmailTransport) => void; transports: ReturnType<typeof useEmailTransports> }) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-slate-600" title="Gmail = your Gmail SMTP connection; SendGrid = your SendGrid API key (both under Integrations). Applies to Email now and to the recurring schedule.">
      <span className="font-semibold uppercase tracking-wide text-slate-500">Via:</span>
      <select value={value} onChange={(e) => onChange(e.target.value as ManaEmailTransport)} className="rounded border border-slate-300 px-1.5 py-0.5 text-slate-800">
        {(Object.keys(MANA_EMAIL_TRANSPORT_LABELS) as ManaEmailTransport[]).map((t) => (
          <option key={t} value={t} disabled={transports.loaded && !transports.configured[t]}>
            {MANA_EMAIL_TRANSPORT_LABELS[t]}
            {transports.loaded && !transports.configured[t] ? ' — set up in Integrations' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

export function GridEmailCard({
  borrowerName,
  email,
  onEmailChange,
  scenario,
  values,
  schedules,
  disabledReason,
  onShowSent,
  template,
  defaultTemplate,
  onTemplateChange,
}: {
  borrowerName: string;
  email: string;
  onEmailChange: (v: string) => void;
  /** Built at click time (the UWM request body reflects the latest form). */
  scenario: () => EmailScenario;
  /** The scenario as edited: its Term and Temporary Buydown selections are the email's rows and columns. */
  values: CanonicalValues;
  schedules: SchedulesResponse | undefined;
  disabledReason: string | null;
  onShowSent: () => void;
  /** The template the MLO chose (null = the scenario's default, `defaultTemplate`). */
  template: ManaEmailTemplate | null;
  defaultTemplate: ManaEmailTemplate;
  onTemplateChange: (t: ManaEmailTemplate | null) => void;
}) {
  const qc = useQueryClient();
  const existing = (schedules?.schedules ?? []).find((s) => s.format === 'grid' && s.borrowerName === borrowerName) ?? null;
  const legacy = (schedules?.legacy ?? []).find((s) => s.source === 'pricing' && s.borrowerName === borrowerName) ?? null;
  // The email's columns = the scenario's Temporary Buydown selection (None = Fixed); its rows = the scenario's terms.
  const programKeys = useMemo(() => programKeysForBuydowns(values.buydown), [values.buydown]);
  const programLabels = UWM_PROGRAMS.filter((p) => programKeys.includes(p.key)).map((p) => p.label);
  const termLabels = fieldValueList(values.term).map((t) => t.replace(/\s*Years?$/i, ''));
  const iv = useInterval(existing);
  const [emailMsg, setEmailMsg] = useState<Msg>(null);
  const [scheduleMsg, setScheduleMsg] = useState<Msg>(null);
  const [preview, setPreview] = useState<PreviewContent | null>(null);

  const [transport, setTransport, transports] = useTransportChoice(existing);
  const payload = () => {
    const s = scenario();
    return { transport, to: email, borrowerName: s.borrowerName, productType: s.productType, inputs: s.inputs, uwmInputs: s.uwmInputs, lenders: s.lenders, programKeys: programKeysForBuydowns(s.inputs.buydown), params: s.params, cypBps: s.cypBps, uwmCategory: s.uwmCategory, template: s.template };
  };
  const previewMut = useMutation({
    mutationFn: () => api<PreviewDto>('/api/mana-pricing/email/grid/preview', { method: 'POST', body: JSON.stringify({ ...payload(), to: email || 'preview@example.com' }) }),
    onSuccess: (r) => setPreview({ title: `Best-rates email for ${borrowerName}`, subject: r.subject, html: r.html, cells: r.cells, rows: [], notes: r.notes }),
    onError: (e) => setEmailMsg({ tone: 'err', text: errText(e, 'Preview failed') }),
  });
  const emailMut = useMutation({
    mutationFn: () => api<{ ok: boolean; sentTo: string }>('/api/mana-pricing/email/grid', { method: 'POST', body: JSON.stringify(payload()) }),
    onSuccess: (r) => setEmailMsg({ tone: 'ok', text: `Sent to ${r.sentTo}` }),
    onError: (e) => setEmailMsg({ tone: 'err', text: errText(e, 'Send failed') }),
  });
  const scheduleMut = useMutation({
    mutationFn: () => {
      const s = scenario();
      return api<{ schedule: ScheduleDto; sentTo?: string; updated?: boolean; scheduledStart?: string }>('/api/mana-pricing/schedules', {
        method: 'POST',
        body: JSON.stringify({
          borrowerName: s.borrowerName,
          toEmail: email,
          format: 'grid',
          productType: s.productType,
          inputs: s.inputs,
          uwmInputs: s.uwmInputs,
          lenders: s.lenders,
          programKeys: programKeysForBuydowns(s.inputs.buydown),
          productIds: [],
          params: s.params,
          cypBps: s.cypBps,
          uwmCategory: s.uwmCategory,
          template: s.template,
          transport,
          intervalMinutes: iv.intervalMinutes,
          startAt: localInputToIso(iv.startLocal),
        }),
      });
    },
    onSuccess: (r) => {
      setScheduleMsg({ tone: 'ok', text: scheduleMessage(r, iv.intervalMinutes) });
      void qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
    },
    onError: (e) => setScheduleMsg({ tone: 'err', text: errText(e, 'Schedule failed') }),
  });

  const baseBlock = disabledReason ?? (!borrowerName ? 'Enter the borrower name' : null);
  const sendBlock = baseBlock ?? (!email ? 'Add the borrower’s email' : null);
  const legacyBlock = legacy ? `${borrowerName} still gets this email from the Pricing module — move that schedule under Scheduled emails first.` : null;
  const templateSelect = (
    <label className="flex items-center gap-1.5 text-xs text-slate-600" title="Each template has its own subject, headline and intro; the rate grid is the same. The default follows the scenario's loan type and purpose.">
      <span className="font-semibold uppercase tracking-wide text-slate-500">Template:</span>
      <select value={template ?? ''} onChange={(e) => onTemplateChange((e.target.value || null) as ManaEmailTemplate | null)} className="rounded border border-slate-300 px-1.5 py-0.5 text-slate-800">
        <option value="">Scenario default — {MANA_EMAIL_TEMPLATE_LABELS[defaultTemplate]}</option>
        {MANA_EMAIL_TEMPLATES.map((t) => (
          <option key={t} value={t}>
            {MANA_EMAIL_TEMPLATE_LABELS[t]}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="surface-card">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-brand-navy-800">Best-rates email</h3>
          <p className="mt-0.5 max-w-xl text-xs text-slate-500" title="The Pricing module's rate grid: each term × program shows the lowest ★ rate across the picked lenders, live and rate sheets (a tie goes to the larger credit), with APR, P&I, the buydown schedule and UWM's MI estimate.">
            Each term × program shows the lowest ★ rate across the picked lenders. Re-priced on every send.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="borrower@email.com"
              className="w-52 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-800 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
            />
            <Button size="sm" variant="ghost" disabled={!!baseBlock || previewMut.isPending} title={baseBlock ?? 'See the email and the lender behind each rate — nothing is sent'} onClick={() => previewMut.mutate()}>
              {previewMut.isPending ? 'Pricing…' : 'Preview'}
            </Button>
            <Button size="sm" disabled={!!sendBlock || emailMut.isPending} title={sendBlock ?? `Re-price and email the best rates to ${email}`} onClick={() => emailMut.mutate()}>
              {emailMut.isPending ? 'Sending…' : 'Email now'}
            </Button>
          </div>
          {emailMsg && <span className={clsx('text-xs', emailMsg.tone === 'ok' ? 'text-emerald-600' : 'text-rose-600')}>{emailMsg.text}</span>}
          <button type="button" onClick={onShowSent} className="text-[11px] text-brand-navy-600 hover:underline">
            Rates sent to this borrower
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-5 py-3 text-xs" title="Change the scenario's Term and Temporary Buydown to change the email's rows and columns.">
        <span className="font-semibold uppercase tracking-wide text-slate-500">Sends:</span>
        <span className="text-slate-700">
          <span className="font-medium">{programLabels.join(' · ')}</span>
          {termLabels.length > 0 && (
            <>
              {' '}for <span className="font-medium">{termLabels.join(' / ')} yr</span>
            </>
          )}
        </span>
        <span className="text-slate-400">from the scenario's Term and Temporary Buydown</span>
        {templateSelect}
        <TransportSelect value={transport} onChange={setTransport} transports={transports} />
      </div>
      <ScheduleControls
        iv={iv}
        existing={existing}
        blockedReason={legacyBlock}
        ready={!sendBlock && iv.intervalOk}
        title={sendBlock ?? (!iv.intervalOk ? 'Minimum interval is every 4 hours' : existing ? `Update ${borrowerName}’s schedule — applies to the next send` : `Send now, then every ${fmtInterval(iv.intervalMinutes)} to ${email}`)}
        pending={scheduleMut.isPending}
        onStart={() => scheduleMut.mutate()}
        msg={scheduleMsg}
        what="re-price and email the best rates"
      />
      {preview && <EmailPreviewDialog content={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Realtor Marketing email
// ---------------------------------------------------------------------------

const fmtRate = (n: number) => `${n.toFixed(3)}%`;

export function RealtorEmailCard({
  borrowerName,
  scenario,
  cells,
  gaps,
  hasQuote,
  ratesAsOf,
  scenarioState,
  onSetupScenario,
  schedules,
  disabledReason,
  onShowSent,
}: {
  borrowerName: string;
  /** Built at click time (the UWM request body reflects the latest form). */
  scenario: () => EmailScenario;
  /** The 3 × 3 table from the current results (with the pinned rates). */
  cells: RealtorCell[];
  /** Rows the current results cannot fill (what the scenario still needs to price). */
  gaps: string[];
  hasQuote: boolean;
  /** When the current results were priced (ISO) — the "rates as of" when the table is sent as shown. */
  ratesAsOf: string | null;
  /** The scenario's state (pricing differs by state) — pre-selects that state's realtors. */
  scenarioState: string | null;
  /** Point the scenario at the five programs (Conventional + FHA 30-yr, Fixed + 5/6 ARM, None + 2/1 lender-paid). */
  onSetupScenario: () => void;
  schedules: SchedulesResponse | undefined;
  disabledReason: string | null;
  onShowSent: () => void;
}) {
  const qc = useQueryClient();
  const existing = (schedules?.schedules ?? []).find((s) => s.format === 'realtor' && s.borrowerName === borrowerName) ?? null;
  // Recipients = the sender's realtor contacts in the picked states (opt-outs left out at send
  // time, everyone BCC'd) plus any extra addresses typed here.
  const summaryQuery = useQuery<RealtorContactsSummary>({ queryKey: ['realtor-contacts-summary'], queryFn: () => api('/api/realtor-contacts/summary'), staleTime: 30_000 });
  const summary = summaryQuery.data;
  const [states, setStates] = useState<string[]>([]);
  const [statesTouched, setStatesTouched] = useState(false);
  const [to, setTo] = useState(existing?.toEmail ?? '');
  const [recipientName, setRecipientName] = useState('');
  useEffect(() => {
    if (existing?.toEmail && !to) setTo(existing.toEmail);
    if (existing?.realtorStates?.length) {
      setStates(existing.realtorStates);
      setStatesTouched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id]);
  // Default to the scenario's state while the MLO hasn't picked states by hand.
  useEffect(() => {
    if (statesTouched) return;
    const sendable = new Set((summary?.states ?? []).filter((s) => s.sendable > 0).map((s) => s.state));
    setStates(scenarioState && sendable.has(scenarioState) ? [scenarioState] : []);
  }, [scenarioState, summary, statesTouched]);
  const iv = useInterval(existing);
  const [emailMsg, setEmailMsg] = useState<Msg>(null);
  const [scheduleMsg, setScheduleMsg] = useState<Msg>(null);
  const [preview, setPreview] = useState<PreviewContent | null>(null);
  const extras = to.split(/[,;\s]+/).filter(Boolean);
  const extrasOk = extras.every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  const fromContacts = states.reduce((n, st) => n + (summary?.states.find((s) => s.state === st)?.sendable ?? 0), 0);
  const optedOutInStates = states.reduce((n, st) => n + (summary?.states.find((s) => s.state === st)?.optedOut ?? 0), 0);
  const recipientCount = fromContacts + extras.length;
  const toggleState = (st: string) => {
    setStatesTouched(true);
    setStates((prev) => (prev.includes(st) ? prev.filter((x) => x !== st) : [...prev, st].sort()));
  };

  // The results already price all five programs → Email now / Preview send the table as shown
  // (instant, like the options tray). Otherwise the server prices the five programs live.
  const asShown = hasQuote && gaps.length === 0 && cells.some((c) => c.winner);
  const [transport, setTransport, transports] = useTransportChoice(existing);
  const payload = () => {
    const s = scenario();
    return { transport, to, realtorStates: states, recipientName, borrowerName: s.borrowerName, productType: s.productType, inputs: s.inputs, uwmInputs: s.uwmInputs, lenders: s.lenders, params: s.params, cypBps: s.cypBps, uwmCategory: s.uwmCategory, realtorPicks: s.realtorPicks, ...(asShown ? { cells, ratesAsOf: ratesAsOf ?? undefined } : {}) };
  };
  const previewMut = useMutation({
    mutationFn: () => api<PreviewDto>('/api/mana-pricing/email/realtor/preview', { method: 'POST', body: JSON.stringify(payload()) }),
    onSuccess: (r) => setPreview({ title: `Realtor Marketing email — ${borrowerName}`, subject: r.subject, html: r.html, cells: r.cells, rows: [], notes: r.notes }),
    onError: (e) => setEmailMsg({ tone: 'err', text: errText(e, 'Preview failed') }),
  });
  const emailMut = useMutation({
    mutationFn: () => api<{ ok: boolean; sentTo: string; recipients: number; excluded: { optedOut: number; invalid: number } }>('/api/mana-pricing/email/realtor', { method: 'POST', body: JSON.stringify(payload()) }),
    onSuccess: (r) => setEmailMsg({ tone: 'ok', text: `Sent (BCC) to ${r.sentTo}${r.excluded.optedOut ? ` — ${r.excluded.optedOut} opted out left out` : ''}` }),
    onError: (e) => setEmailMsg({ tone: 'err', text: errText(e, 'Send failed') }),
  });
  const scheduleMut = useMutation({
    mutationFn: () => {
      const s = scenario();
      return api<{ schedule: ScheduleDto; sentTo?: string; updated?: boolean; scheduledStart?: string }>('/api/mana-pricing/schedules', {
        method: 'POST',
        body: JSON.stringify({
          borrowerName: s.borrowerName,
          toEmail: to,
          realtorStates: states,
          format: 'realtor',
          recipientName,
          realtorPicks: s.realtorPicks,
          productType: s.productType,
          inputs: s.inputs,
          uwmInputs: s.uwmInputs,
          lenders: s.lenders,
          programKeys: [],
          productIds: [],
          params: s.params,
          cypBps: s.cypBps,
          uwmCategory: s.uwmCategory,
          transport,
          intervalMinutes: iv.intervalMinutes,
          startAt: localInputToIso(iv.startLocal),
        }),
      });
    },
    onSuccess: (r) => {
      setScheduleMsg({ tone: 'ok', text: scheduleMessage(r, iv.intervalMinutes) });
      void qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
    },
    onError: (e) => setScheduleMsg({ tone: 'err', text: errText(e, 'Schedule failed') }),
  });

  const baseBlock = disabledReason ?? (!borrowerName ? 'Enter a scenario name' : null);
  const sendBlock = baseBlock ?? (!extrasOk ? 'Check the extra email addresses' : recipientCount === 0 ? 'Pick a state with realtor contacts, or add an address' : null);
  const pinnedCount = cells.filter((c) => c.winner?.pinned).length;
  const cellOf = (row: string, column: string) => cells.find((c) => c.row === row && c.column === column) ?? null;
  const stateChips = (summary?.states ?? []).filter((s) => s.total > 0);

  return (
    <div className="surface-card">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-brand-navy-800">Realtor Marketing email</h3>
          <p className="mt-0.5 max-w-xl text-xs text-slate-500" title="Five programs × par / 1 point / 2 points: par is the results' Best rate (lowest ★ rate across the picked lenders), or the desired-price rate when no ★ rate is offered; 1 / 2 points are the lowest rates whose total discount points are at most 1 / 2 — what a buyer gets by spending a full point or two, whatever par costs that day — or the rates you pinned in the results (Par · 1 pt · 2 pts buttons). No lender or borrower names.">
            30-yr conventional 5/6 ARM · 30-yr fixed · lender-paid 2/1 buydown · 30-yr fixed FHA · 30-yr FHA 5/1 ARM at par (the Best rate, else the desired-price rate), and the lowest rates for a total charge of 1 point and of 2 points — across the picked lenders, or the rates you pin in the results. Email now sends the table as shown; recurring sends re-price it. Below the table, the email carries your Conventional Homebuyer Incentives (up to $600 appraisal incentive, lender-paid temporary buydowns).
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <TransportSelect value={transport} onChange={setTransport} transports={transports} />
            <input
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Greeting name (optional)"
              title='"Hi Sarah," — leave blank for a general greeting when the email goes to many realtors'
              className="w-40 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-800 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
            />
            <Button size="sm" variant="ghost" disabled={!!baseBlock || previewMut.isPending} title={baseBlock ?? `See the email and the lender behind each rate — nothing is sent${asShown ? '' : ' (prices the five programs live, about 1–2 minutes)'}`} onClick={() => previewMut.mutate()}>
              {previewMut.isPending ? (asShown ? 'Building…' : 'Pricing…') : 'Preview'}
            </Button>
            <Button size="sm" disabled={!!sendBlock || emailMut.isPending} title={sendBlock ?? `${asShown ? 'Email the table as shown' : 'Price the five programs live and email the table'} to ${recipientCount} realtor${recipientCount === 1 ? '' : 's'} (BCC — nobody sees the other addresses)`} onClick={() => emailMut.mutate()}>
              {emailMut.isPending ? 'Sending…' : recipientCount > 1 ? `Email ${recipientCount} realtors` : 'Email now'}
            </Button>
          </div>
          {emailMsg && <span className={clsx('text-xs', emailMsg.tone === 'ok' ? 'text-emerald-600' : 'text-rose-600')}>{emailMsg.text}</span>}
          <button type="button" onClick={onShowSent} className="text-[11px] text-brand-navy-600 hover:underline">
            Rate sheets sent
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-100 bg-slate-50/60 px-5 py-2.5 text-xs">
        <span className="font-semibold uppercase tracking-wide text-slate-500">Send to realtors in:</span>
        {summaryQuery.isLoading ? (
          <span className="text-slate-400">loading your contacts…</span>
        ) : stateChips.length === 0 ? (
          <span className="text-slate-500">
            no realtor contacts yet —{' '}
            <Link to="/mana-pricing/realtors" className="font-medium text-brand-navy-600 hover:underline">
              add or import them
            </Link>
          </span>
        ) : (
          stateChips.map((s) => {
            const on = states.includes(s.state);
            return (
              <button
                key={s.state}
                type="button"
                onClick={() => toggleState(s.state)}
                disabled={s.sendable === 0 && !on}
                title={`${stateName(s.state)}: ${s.sendable} realtor${s.sendable === 1 ? '' : 's'} to email${s.optedOut ? `, ${s.optedOut} opted out` : ''}${s.state === scenarioState ? ' · the scenario’s state' : ''}`}
                className={clsx(
                  'rounded-full px-2.5 py-0.5 font-semibold ring-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                  on ? 'bg-brand-navy-700 text-white ring-brand-navy-700' : 'bg-white text-slate-600 ring-slate-300 hover:bg-slate-100',
                )}
              >
                {s.state} <span className={clsx('font-normal', on ? 'text-brand-navy-100' : 'text-slate-400')}>{s.sendable}</span>
              </button>
            );
          })
        )}
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="+ extra addresses, comma-separated"
          className="w-60 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-800 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
        />
        <span className="text-slate-500">
          {recipientCount > 0 ? `${recipientCount} recipient${recipientCount === 1 ? '' : 's'}, BCC` : 'no recipients yet'}
          {optedOutInStates ? ` · ${optedOutInStates} opted out excluded` : ''}
        </span>
        <Link to="/mana-pricing/realtors" className="ml-auto font-medium text-brand-navy-600 hover:underline">
          Manage contacts
        </Link>
      </div>
      <div className="px-5 py-3">
        {hasQuote ? (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <tr>
                <th className="py-1 pr-3">Loan program</th>
                {REALTOR_COLUMNS.map((c) => (
                  <th key={c.key} className="py-1 pr-3">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {REALTOR_ROWS.map((row) => (
                <tr key={row.key} className="border-t border-slate-100 align-top">
                  <td className="py-1.5 pr-3 font-medium text-slate-700">{row.label}</td>
                  {REALTOR_COLUMNS.map((col) => {
                    const w = cellOf(row.key, col.key)?.winner ?? null;
                    return (
                      <td key={col.key} className="py-1.5 pr-3">
                        {w ? (
                          <>
                            <span className={clsx('font-semibold tabular-nums', w.pinned ? 'text-brand-gold-800' : 'text-slate-800')} title={`${w.lender} · ${w.product}${w.pinned ? ' · pinned' : ''}${col.key === 'par' ? (w.star ? ' · ★ best rate' : ' · desired-price rate (no ★ rate offered)') : ''}`}>
                              {col.key === 'par' && w.star && <span className="mr-0.5 text-brand-gold-600">★</span>}
                              {fmtRate(w.rate)}
                              {w.pinned && <span className="ml-1 text-[10px] font-normal">pinned</span>}
                            </span>
                            <div className="text-[11px] text-slate-500">
                              APR {fmtRate(w.apr)} · {realtorPointsLabel(w.price)}
                            </div>
                          </>
                        ) : (
                          <span className="text-amber-700">{col.key !== 'par' && cellOf(row.key, 'par')?.winner ? `No rate within ${col.maxPoints} pt${col.maxPoints === 1 ? '' : 's'} today` : 'Call for pricing'}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-xs text-slate-500">Get pricing to see the table; a send prices the five programs whatever the scenario selects.</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
          {gaps.length > 0 && hasQuote ? (
            <>
              <span className="text-amber-700">Not in these results: {gaps.join(' · ')} — the send still prices them; to pin rates for them,</span>
              <button type="button" onClick={onSetupScenario} className="font-medium text-brand-navy-600 hover:underline">
                set the scenario to the five programs
              </button>
              <span>and Get pricing again.</span>
            </>
          ) : hasQuote ? (
            <span>Par = the results' Best rate (lowest ★ rate across the picked lenders), or the desired-price rate when no ★ rate is offered; 1 / 2 points = the lowest rate costing at most 1 / 2 points more than that par rate. Pin any rate from the results with its Par · 1 pt · 2 pts buttons{pinnedCount ? ` (${pinnedCount} pinned)` : ''}.</span>
          ) : null}
        </div>
      </div>
      <ScheduleControls
        iv={iv}
        existing={existing}
        blockedReason={null}
        ready={!sendBlock && iv.intervalOk}
        title={sendBlock ?? (!iv.intervalOk ? 'Minimum interval is every 4 hours' : existing ? 'Update the realtor blast — applies to the next send' : `Send now, then every ${fmtInterval(iv.intervalMinutes)} to your realtors in ${states.join(', ') || 'the extra addresses'} (the list is read fresh every send)`)}
        pending={scheduleMut.isPending}
        onStart={() => scheduleMut.mutate()}
        msg={scheduleMsg}
        what="re-price and email the realtor rate table"
      />
      {preview && <EmailPreviewDialog content={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Options tray + options email
// ---------------------------------------------------------------------------

export function OptionsTray({
  borrowerName,
  email,
  onEmailChange,
  scenario,
  tray,
  ratesAsOf,
  onRemove,
  onAddManual,
  hasQuote,
  schedules,
}: {
  borrowerName: string;
  email: string;
  onEmailChange: (v: string) => void;
  scenario: () => EmailScenario;
  tray: LenderRateRow[];
  ratesAsOf: string | null;
  onRemove: (key: string) => void;
  onAddManual: (row: LenderRateRow) => void;
  hasQuote: boolean;
  schedules: SchedulesResponse | undefined;
}) {
  const qc = useQueryClient();
  const [manualOpen, setManualOpen] = useState(false);
  const emptyManual: LenderRateRow = { lender: '', product: '', rate: '', points: '', monthlyPayment: '', lenderCredit: '', notes: '' };
  const [manual, setManual] = useState<LenderRateRow>(emptyManual);
  const [emailMsg, setEmailMsg] = useState<Msg>(null);
  const [scheduleMsg, setScheduleMsg] = useState<Msg>(null);
  const existing = (schedules?.schedules ?? []).find((s) => s.format === 'options' && s.borrowerName === borrowerName) ?? null;
  const legacy = (schedules?.legacy ?? []).find((s) => s.source === 'other_lenders' && s.borrowerName === borrowerName) ?? null;
  const iv = useInterval(existing);
  const productIds = useMemo(() => tray.map((t) => t.productId).filter((id): id is string => !!id), [tray]);
  const manualCount = tray.length - productIds.length;

  const [transport, setTransport, transports] = useTransportChoice(existing);
  const emailMut = useMutation({
    mutationFn: () => {
      const s = scenario();
      return api<{ ok: boolean; sentTo: string }>('/api/mana-pricing/email/options', { method: 'POST', body: JSON.stringify({ transport, to: email, borrowerName: s.borrowerName, inputs: s.inputs, rates: tray, ratesAsOf }) });
    },
    onSuccess: (r) => setEmailMsg({ tone: 'ok', text: `Sent to ${r.sentTo}` }),
    onError: (e) => setEmailMsg({ tone: 'err', text: errText(e, 'Send failed') }),
  });
  const scheduleMut = useMutation({
    mutationFn: () => {
      const s = scenario();
      return api<{ schedule: ScheduleDto; sentTo?: string; updated?: boolean; scheduledStart?: string }>('/api/mana-pricing/schedules', {
        method: 'POST',
        body: JSON.stringify({
          borrowerName: s.borrowerName,
          toEmail: email,
          format: 'options',
          productType: s.productType,
          inputs: s.inputs,
          uwmInputs: s.uwmInputs,
          lenders: s.lenders,
          programKeys: [],
          productIds,
          params: null,
          cypBps: s.cypBps,
          uwmCategory: s.uwmCategory,
          transport,
          intervalMinutes: iv.intervalMinutes,
          startAt: localInputToIso(iv.startLocal),
        }),
      });
    },
    onSuccess: (r) => {
      setScheduleMsg({ tone: 'ok', text: scheduleMessage(r, iv.intervalMinutes) });
      void qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
    },
    onError: (e) => setScheduleMsg({ tone: 'err', text: errText(e, 'Schedule failed') }),
  });

  if (!hasQuote && tray.length === 0) return null;
  const emailBlock = !email ? 'Add the borrower’s email' : !borrowerName ? 'Enter the borrower name' : tray.length === 0 ? 'Add at least one option' : null;
  const scheduleBlock = !email ? 'Add the borrower’s email' : !borrowerName ? 'Enter the borrower name' : productIds.length === 0 ? 'Add at least one priced option (manual rows can’t be re-priced)' : null;
  const legacyBlock = legacy ? `${borrowerName} still has a schedule filed under the retired Other Lenders Pricing module (it no longer sends) — move it under Scheduled emails first.` : null;

  return (
    <div className="surface-card">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-brand-navy-800">Options for {borrowerName || 'the borrower'}</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {tray.length === 0
              ? 'Use + on a result to shortlist it, or enter a rate manually. The email never shows the lender.'
              : `${tray.length} option${tray.length === 1 ? '' : 's'}${manualCount ? ` · ${manualCount} manual` : ''} · as of ${fmtWhen(ratesAsOf)}`}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="borrower@email.com"
              className="w-52 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-800 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
            />
            <TransportSelect value={transport} onChange={setTransport} transports={transports} />
            <Button size="sm" disabled={!!emailBlock || emailMut.isPending} title={emailBlock ?? `Email these options to ${email}`} onClick={() => emailMut.mutate()}>
              {emailMut.isPending ? 'Sending…' : 'Email options'}
            </Button>
          </div>
          {emailMsg && <span className={clsx('text-xs', emailMsg.tone === 'ok' ? 'text-emerald-600' : 'text-rose-600')}>{emailMsg.text}</span>}
        </div>
      </div>

      {tray.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {tray.map((r) => {
            const k = trayKey(r);
            return (
              <li key={k} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-2.5 text-sm">
                <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                  <p className="truncate font-medium text-slate-800">
                    {r.product || '—'}
                    {!r.productId && (
                      <Badge tone="neutral" className="ml-2 align-middle">
                        manual
                      </Badge>
                    )}
                  </p>
                  <p className="truncate text-[11px] text-slate-500">
                    {r.lender}
                    {r.notes ? ` · ${r.notes}` : ''}
                  </p>
                </div>
                <span className="w-20 text-right font-semibold tabular-nums text-slate-800">{r.rate || '—'}</span>
                <span className="w-16 text-right tabular-nums text-slate-600" title="Points / price">
                  {r.points || '—'}
                </span>
                <span className="w-20 text-right tabular-nums text-slate-800" title="Monthly P&I">
                  {r.monthlyPayment || '—'}
                </span>
                <span className={clsx('w-24 text-right tabular-nums', r.lenderCredit.startsWith('(') ? 'text-rose-600' : 'text-emerald-600')} title="Lender credit (cost)">
                  {r.lenderCredit || '—'}
                </span>
                <button type="button" onClick={() => onRemove(k)} className="text-slate-400 hover:text-rose-600" title="Remove">
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="border-t border-slate-100 px-5 py-3">
        <button type="button" onClick={() => setManualOpen((v) => !v)} className="text-xs font-medium text-brand-navy-600 hover:underline">
          {manualOpen ? 'Hide manual entry' : 'Enter a rate manually'} <span className="font-normal text-slate-400">(for a lender without pricing here yet)</span>
        </button>
        {manualOpen && (
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            {(
              [
                ['lender', 'Lender'],
                ['product', 'Product'],
                ['rate', 'Rate'],
                ['points', 'Points / price'],
                ['monthlyPayment', 'Monthly P&I'],
                ['lenderCredit', 'Lender credit'],
                ['notes', 'Notes'],
              ] as Array<[keyof LenderRateRow, string]>
            ).map(([id, label]) => (
              <input
                key={id}
                value={String(manual[id] ?? '')}
                placeholder={label}
                onChange={(e) => setManual((m) => ({ ...m, [id]: e.target.value }))}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-800 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
              />
            ))}
            <div className="col-span-2 flex items-center gap-2 sm:col-span-4 lg:col-span-7">
              <Button
                size="sm"
                variant="secondary"
                disabled={!(manual.lender.trim() || manual.product.trim() || manual.rate.trim())}
                onClick={() => {
                  onAddManual({ ...manual, productId: undefined });
                  setManual(emptyManual);
                }}
              >
                Add to options
              </Button>
              <span className="text-[11px] text-slate-400">Manual rows are emailed as typed and aren’t included in recurring sends.</span>
            </div>
          </div>
        )}
      </div>

      <ScheduleControls
        iv={iv}
        existing={existing}
        blockedReason={legacyBlock}
        ready={!scheduleBlock && iv.intervalOk}
        title={scheduleBlock ?? (!iv.intervalOk ? 'Minimum interval is every 4 hours' : existing ? `Update ${borrowerName}’s schedule — applies to the next send` : `Send now, then every ${fmtInterval(iv.intervalMinutes)} to ${email}`)}
        pending={scheduleMut.isPending}
        onStart={() => scheduleMut.mutate()}
        msg={scheduleMsg}
        what="re-price these options and email"
      />
    </div>
  );
}
